import {
  AuditDetail,
  OriginResponse,
  TokenResponse,
} from "../types/epc-interfaces";
import { ensureHttpsProtocol } from "../utils/ensureHttpsProtocol";
import { withRetry } from "../utils/retry";

const ENCOMPASS_SERVER_URL = process.env.NEXT_PUBLIC_LL_WEB_ENCOMPASS_API_HOST;

export async function getToken({
  originId,
  isUat,
  partnerAccessToken,
  sessionId,
}: {
  originId: string;
  isUat: boolean;
  partnerAccessToken: string;
  sessionId?: string | null;
}): Promise<string> {
  console.log("getToken called", {
    originId,
    isUat,
    hasPartnerAccessToken: !!partnerAccessToken,
    sessionId,
  });
  const data = await apiRequest<TokenResponse>(
    "auth/encompass",
    {
      method: "POST",
      headers: {
        "x-origin-id": originId,
        "x-partner-access-token": partnerAccessToken,
        "x-is-uat": String(isUat),
        ...(sessionId && { "x-session-id": sessionId }),
      },
    },
    "getToken",
    ENCOMPASS_SERVER_URL,
  );
  const token = data?.access_token;
  return token;
}

export async function getOrigin({
  originId,
  token,
  partnerAccessToken,
  isUat,
  sessionId,
}: {
  originId: string;
  token: string;
  partnerAccessToken: string;
  isUat: boolean;
  sessionId?: string | null;
}): Promise<OriginResponse> {
  const path = "integrations/origin";
  console.log("Getting origin from path: ", { path, sessionId });
  const originResponse = await apiRequest<OriginResponse>(
    path,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-is-uat": String(isUat),
        ...(sessionId && { "x-session-id": sessionId }),
      },
      body: JSON.stringify({ originId, partnerAccessToken, isUat }),
    },
    "getOrigin",
  );
  console.log(
    "Origin data retrieved successfully",
    JSON.stringify(originResponse),
  );
  return originResponse;
}

export async function getAuditDetails(
  transactionId: string,
  token: string,
  sessionId?: string | null,
): Promise<AuditDetail | null> {
  try {
    return await apiRequest<AuditDetail>(
      `integrations/audit-details/${transactionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(sessionId && { "x-session-id": sessionId }),
        },
      },
      "getAuditDetails",
    );
  } catch (error) {
    // If it's a 404, audit details don't exist yet - return null
    if (error instanceof CustomError && error.context?.status === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit,
  operation: string,
  baseUrl?: string,
): Promise<T> {
  const fullUrl = ensureHttpsProtocol(
    `${baseUrl ?? ENCOMPASS_SERVER_URL}/${endpoint}`,
  );

  return withRetry(
    async () => {
      const res = await fetch(fullUrl, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
        },
      });

      // Handle error responses
      if (!res.ok) {
        const errorBody = await res.text().catch(() => "");
        throw new CustomError(
          `${operation} request failed: ${res.status} ${res.statusText}`,
          "API_REQUEST_FAILED",
          {
            operation,
            status: res.status,
            statusText: res.statusText,
            endpoint: fullUrl,
            retryable: res.status >= 500,
            errorBody: errorBody.substring(0, 200),
          },
        );
      }

      const text = await res.text();

      try {
        return JSON.parse(text) as T;
      } catch (parseError) {
        throw new CustomError(
          `${operation} response is not valid JSON: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
          "API_INVALID_JSON",
          {
            operation,
            status: res.status,
            endpoint: fullUrl,
            responseText: text.substring(0, 200),
            retryable: false,
          },
        );
      }
    },
    {
      operationName: operation,
      shouldRetry: (error) => {
        if (error instanceof CustomError) {
          const status = error.context?.status;
          if (typeof status === "number") {
            if (status === 404 || error.operation === "API_INVALID_JSON") {
              return false;
            }
            return status >= 500;
          }

          return false;
        }

        // Retry network errors
        const errorName = getErrorName(error);
        return errorName === "TypeError" || errorName === "AbortError";
      },
    },
  );
}

export class CustomError extends Error {
  public readonly operation: string;
  public readonly context?: Record<string, unknown>;

  constructor(
    message: string,
    operation: string,
    context?: Record<string, unknown>,
  ) {
    super(message);
    this.name = this.constructor.name;
    this.operation = operation;
    this.context = context;
  }
}

const getErrorName = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.name;
  }

  if (error && typeof error === "object" && "name" in error) {
    const name = (error as { name?: unknown }).name;
    return typeof name === "string" ? name : undefined;
  }

  return undefined;
};
